import { isSameDay } from 'date-fns'
import { useMemo } from 'react'

const safeDate = (d: Date | string | null | undefined): Date | null => {
  if (!d) return null
  const date = new Date(d)
  return isNaN(date.getTime()) ? null : date
}

const range = (start: number, end: number) => {
  const result = []
  for (let i = start; i < end; i++) {
    result.push(i)
  }
  return result
}

type TimeRestrictionParams = {
  shippingDate: Date | string | null | undefined
  deliveryDate: Date | string | null | undefined
  departureStart: Date | string | null | undefined
  departureEnd: Date | string | null | undefined
  arrivalStart: Date | string | null | undefined
  arrivalEnd: Date | string | null | undefined
}

type DisabledTime = {
  disabledHours: () => number[]
  disabledMinutes: (selectedHour: number) => number[]
}

export const useTimeRestriction = ({
  shippingDate,
  deliveryDate,
  departureStart,
  departureEnd,
  arrivalStart,
  arrivalEnd
}: TimeRestrictionParams) => {
  const isSameDayDelivery = useMemo(() => {
    const sDate = safeDate(shippingDate)
    const dDate = safeDate(deliveryDate)
    if (!sDate || !dDate) return false
    return isSameDay(sDate, dDate)
  }, [shippingDate, deliveryDate])

  // 発地の到着時間制限(開始)の無効化ロジック
  const departureStartRestriction = useMemo((): DisabledTime => {
    // 1. 発地開始 <= 発地終了となっていること
    if (departureEnd) {
      const depEnd = safeDate(departureEnd)
      if (depEnd) {
        const maxHour = depEnd.getHours()
        const maxMinute = depEnd.getMinutes()
        return {
          disabledHours: () => range(maxHour + 1, 24),
          disabledMinutes: (selectedHour: number) =>
            selectedHour === maxHour ? range(maxMinute + 1, 60) : []
        }
      }
    }
    return {
      disabledHours: () => [],
      disabledMinutes: () => []
    }
  }, [departureEnd])

  // 発地の到着時間制限(終了)の無効化ロジック
  const departureEndRestriction = useMemo((): DisabledTime => {
    let minHour = 0
    let minMinute = 0
    let maxHour = 23
    let maxMinute = 59
    let hasMinConstraint = false
    let hasMaxConstraint = false

    // 1. 発地開始時間より前は選択不可
    if (departureStart) {
      const depStart = safeDate(departureStart)
      if (depStart) {
        minHour = depStart.getHours()
        minMinute = depStart.getMinutes()
        hasMinConstraint = true
      }
    }

    // 2. 同日の場合: 発地終了時間は着地開始時間と同じもしくは前でなければならない
    if (isSameDayDelivery && arrivalStart) {
      const arrStart = safeDate(arrivalStart)
      if (arrStart) {
        maxHour = arrStart.getHours()
        maxMinute = arrStart.getMinutes() // 同じ分も選択可能
        hasMaxConstraint = true
      }
    }

    // 3. 同日の場合: 発地終了時間は着地終了時間と同じもしくは前でなければならない
    if (isSameDayDelivery && arrivalEnd) {
      const arrEnd = safeDate(arrivalEnd)
      if (arrEnd) {
        maxHour = arrEnd.getHours()
        maxMinute = arrEnd.getMinutes() // 同じ分も選択可能
        hasMaxConstraint = true
      }
    }

    // 最小制約と最大制約が矛盾する場合（最小 > 最大）
    if (
      hasMinConstraint &&
      hasMaxConstraint &&
      (minHour > maxHour || (minHour === maxHour && minMinute > maxMinute))
    ) {
      // すべての時間を無効化
      return {
        disabledHours: () => range(0, 24),
        disabledMinutes: () => range(0, 60)
      }
    }

    return {
      disabledHours: () => {
        const disabled = []
        for (let i = 0; i < 24; i++) {
          if (i < minHour || i > maxHour) {
            disabled.push(i)
          }
        }
        return disabled
      },
      disabledMinutes: (selectedHour: number) => {
        if (selectedHour < minHour || selectedHour > maxHour) {
          return range(0, 60)
        }
        if (selectedHour === minHour && selectedHour === maxHour) {
          return [...range(0, minMinute), ...range(maxMinute + 1, 60)]
        }
        if (selectedHour === minHour) {
          return range(0, minMinute)
        }
        if (selectedHour === maxHour) {
          return range(maxMinute + 1, 60)
        }
        return []
      }
    }
  }, [departureStart, isSameDayDelivery, arrivalStart, arrivalEnd])

  // 着地の到着時間制限(開始)の無効化ロジック
  const arrivalStartRestriction = useMemo((): DisabledTime => {
    let minHour = 0
    let minMinute = 0
    let maxHour = 23
    let maxMinute = 59
    let hasMinConstraint = false
    let hasMaxConstraint = false

    // 1. 同日の場合: 着地開始時間は発地開始時間（なければ発地終了時間）と同じか後でなければならない
    // 着地開始時間と発地終了時間が同じでもOK
    if (isSameDayDelivery) {
      const depEnd = safeDate(departureEnd)
      const depStart = safeDate(departureStart)

      if (depEnd && depStart) {
        // 両方ある場合 -> 発地開始基準
        minHour = depStart.getHours()
        minMinute = depStart.getMinutes()
        hasMinConstraint = true
      } else if (depEnd) {
        // 発地終了のみ -> 発地終了基準
        minHour = depEnd.getHours()
        minMinute = depEnd.getMinutes()
        hasMinConstraint = true
      } else if (depStart) {
        // 発地開始のみ -> 発地開始基準
        minHour = depStart.getHours()
        minMinute = depStart.getMinutes()
        hasMinConstraint = true
      }
    }

    // 2. 着地終了時間より後は選択不可
    if (arrivalEnd) {
      const arrEnd = safeDate(arrivalEnd)
      if (arrEnd) {
        maxHour = arrEnd.getHours()
        maxMinute = arrEnd.getMinutes()
        hasMaxConstraint = true
      }
    }

    // 最小制約と最大制約が矛盾する場合（最小 > 最大）
    if (
      hasMinConstraint &&
      hasMaxConstraint &&
      (minHour > maxHour || (minHour === maxHour && minMinute > maxMinute))
    ) {
      // すべての時間を無効化
      return {
        disabledHours: () => range(0, 24),
        disabledMinutes: () => range(0, 60)
      }
    }

    return {
      disabledHours: () => {
        const disabled = []
        for (let i = 0; i < 24; i++) {
          if (i < minHour || i > maxHour) {
            disabled.push(i)
          }
        }
        return disabled
      },
      disabledMinutes: (selectedHour: number) => {
        if (selectedHour < minHour || selectedHour > maxHour) {
          return range(0, 60)
        }
        if (selectedHour === minHour && selectedHour === maxHour) {
          return [...range(0, minMinute), ...range(maxMinute + 1, 60)]
        }
        if (selectedHour === minHour) {
          return range(0, minMinute)
        }
        if (selectedHour === maxHour) {
          return range(maxMinute + 1, 60)
        }
        return []
      }
    }
  }, [isSameDayDelivery, departureEnd, departureStart, arrivalEnd])

  // 着地の到着時間制限(終了)の無効化ロジック
  const arrivalEndRestriction = useMemo((): DisabledTime => {
    let minHour = 0
    let minMinute = 0

    // 1. 同日の場合: 着地終了時間は発地終了時間（なければ開始時間）より同じか後でなければならない
    if (isSameDayDelivery) {
      const depEnd = safeDate(departureEnd)
      const depStart = safeDate(departureStart)

      if (depEnd) {
        // 発地終了のみ -> 発地終了基準
        minHour = depEnd.getHours()
        minMinute = depEnd.getMinutes()
      } else if (depStart) {
        // 発地開始のみ -> 発地開始基準
        minHour = depStart.getHours()
        minMinute = depStart.getMinutes()
      }
    }

    // 2. 着地開始時間より前は選択不可
    if (arrivalStart) {
      const arrStart = safeDate(arrivalStart)
      if (arrStart) {
        minHour = arrStart.getHours()
        minMinute = arrStart.getMinutes()
      }
    }

    return {
      disabledHours: () => {
        const disabled = []
        for (let i = 0; i < 24; i++) {
          if (i < minHour) {
            disabled.push(i)
          }
        }
        return disabled
      },
      disabledMinutes: (selectedHour: number) => {
        if (selectedHour < minHour) {
          return range(0, 60)
        }
        if (selectedHour === minHour) {
          return range(0, minMinute)
        }
        return []
      }
    }
  }, [arrivalStart, isSameDayDelivery, departureEnd, departureStart])

  return {
    isSameDayDelivery,
    departureStartRestriction,
    departureEndRestriction,
    arrivalStartRestriction,
    arrivalEndRestriction
  }
}
